import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import { createTextColumn } from '@ipfabric/utils/table/columns';
import type { RowDragState } from 'components/LazyRowContainer/LazyRowContainer';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useDraggableTable } from 'components/Table/model/useDraggableTable';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { type MouseEvent, useCallback, useMemo } from 'react';
import { isTestRuleAvailable } from './ruleEditorValues';

export type RuleListTableRow = {
  rule: SiteSeparationRule;
  id: string;
  name: string;
  description: string;
};

const getRuleName = (rule: SiteSeparationRule): string => {
  switch (rule.type) {
    case 'regexHostname':
      return 'Hostname regex';
    case 'regexSnmpLocation':
      return 'SNMP location regex';
    case 'regexCloudResourceId':
      return 'Cloud resource ID regex';
    case 'cloudResourceTag':
      return 'Cloud Tags';
    case 'routingSwitching':
      return 'Routing & switching';
    case 'slug':
      return 'Cloud slug';
    default:
      rule satisfies never;
      stumble('Unknown rule');
      return 'Unknown rule';
  }
};

export const renderRuleNameCell = (
  row: RuleListTableRow,
  onTestRule: (rule: SiteSeparationRule) => void,
) => (
  <div className='inline-block align-middle'>
    <div className='leading-none'>{row.name}</div>
    {isTestRuleAvailable(row.rule.type) && (
      <div
        data-testid={`site-separation-rule-${row.rule.note}-show-test-button`}
        className='leading-none'
      >
        <Link
          renderAs='button'
          onClick={() => {
            onTestRule(row.rule);
          }}
        >
          Test rule
        </Link>
      </div>
    )}
  </div>
);

type RuleListTableProps = {
  rules: SiteSeparationRule[];
  onRulesChange: (rules: SiteSeparationRule[]) => void;
  onAddRuleClick: () => void;
  onEditRule?: (rule: SiteSeparationRule) => void;
  onRemoveRule?: (ruleId: string) => void;
  onTestRule: (rule: SiteSeparationRule) => void;
};

export const RuleListTable = (props: RuleListTableProps) => {
  const tableData = useMemo<RuleListTableRow[]>(
    () =>
      props.rules.map((rule) => ({
        rule,
        id: rule.id,
        name: getRuleName(rule),
        description: rule.note,
      })),
    [props.rules],
  );

  const draggable = useDraggableTable({
    data: props.rules,
    onDrop: props.onRulesChange,
    getRowIdentity: (row) => row.id,
  });

  const columns = useMemo(
    () => [
      createFrontendOnlyColumn<RuleListTableRow>({
        key: 'drag',
        title: '',
        disabledSort: true,
        fixedWidth: true,
        width: 40,
        render: (item) => (
          <Icon
            glyph='fa-grip-dots-vertical'
            size='sm'
            className='cursor-grab'
            onMouseDown={() => {
              const index = props.rules.findIndex(
                (rule) => rule.id === item.id,
              );
              if (index >= 0) {
                draggable.onRowDragStart(index);
              }
            }}
          />
        ),
      }),
      createTextColumn({
        key: 'name',
        title: 'Name',
        renderContent: ({ row }: { row: RuleListTableRow }) => ({
          content: renderRuleNameCell(row, props.onTestRule),
        }),
      }),
      createTextColumn({
        key: 'description',
        title: 'Description',
      }),
      createFrontendOnlyColumn<RuleListTableRow>({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        fixedWidth: true,
        width: 80,
        render: ({ rule }) => (
          <div className='flex items-center gap-xs'>
            <TableButton
              dataTestId={`site-separation-rule-${rule.note}-edit-icon`}
              icon='fa-pen'
              size='small'
              variant='plain'
              type='button'
              onClick={() => {
                props.onEditRule?.(rule);
              }}
            />
            <TableButton
              dataTestId={`site-separation-rule-${rule.note}-remove-icon`}
              icon='fa-trash-can'
              size='small'
              variant='plain'
              accent='negative'
              type='button'
              onClick={() => {
                props.onRemoveRule?.(rule.id);
              }}
            />
          </div>
        ),
      }),
    ],
    [
      props.rules,
      draggable.onRowDragStart,
      props.onEditRule,
      props.onRemoveRule,
      props.onTestRule,
    ],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableData,
  });
  const renderedRows = frontendSimpleFilter.data as RuleListTableRow[];
  const sourceIndexById = useMemo(
    () => new Map(props.rules.map((row, index) => [row.id, index] as const)),
    [props.rules],
  );
  const renderedIndexById = useMemo(
    () => new Map(renderedRows.map((row, index) => [row.id, index] as const)),
    [renderedRows],
  );

  const getRenderedDropBeforeIndex = useCallback(
    (dropBeforeIndex: number | null): number | null => {
      if (dropBeforeIndex === null) {
        return null;
      }
      if (dropBeforeIndex === props.rules.length) {
        return renderedRows.length;
      }
      const beforeRowId = props.rules[dropBeforeIndex]?.id;
      if (!beforeRowId) {
        return null;
      }
      return renderedIndexById.get(beforeRowId) ?? null;
    },
    [props.rules, renderedRows.length, renderedIndexById],
  );

  const rowDragState = useMemo<RowDragState>(() => {
    const draggedRowId =
      draggable.rowDragState.draggedIndex === null
        ? null
        : (props.rules[draggable.rowDragState.draggedIndex]?.id ?? null);
    return {
      draggedIndex:
        draggedRowId === null
          ? null
          : (renderedIndexById.get(draggedRowId) ?? null),
      dropBeforeIndex: getRenderedDropBeforeIndex(
        draggable.rowDragState.dropBeforeIndex,
      ),
    };
  }, [
    draggable.rowDragState.draggedIndex,
    draggable.rowDragState.dropBeforeIndex,
    getRenderedDropBeforeIndex,
    props.rules,
    renderedIndexById,
  ]);

  const onRowMouseMove = useCallback(
    (renderedRowIndex: number, e: MouseEvent) => {
      const rowId = renderedRows[renderedRowIndex]?.id;
      if (!rowId) {
        return;
      }
      const sourceIndex = sourceIndexById.get(rowId);
      if (sourceIndex === undefined) {
        return;
      }
      draggable.onRowMouseMove(sourceIndex, e);
    },
    [draggable.onRowMouseMove, renderedRows, sourceIndexById],
  );

  const menuItems = useMemo(
    () => [
      {
        standalone: true,
        elementWithKey: (
          <Button
            data-testid='site-separation-add-rule'
            icon='fa-plus'
            key='site-separation-add-new'
            onClick={props.onAddRuleClick}
            size='small'
            type='button'
            variant='outline'
          >
            Add new
          </Button>
        ),
      } satisfies TableActionsItem,
    ],
    [props.onAddRuleClick],
  );

  return (
    <Table
      borderless
      className='flex-1 rounded-b bg-transparent'
      columns={columns}
      rowIdField='id'
      rowHeight={60}
      title='Rule list'
      description='Define how devices are assigned to sites. Rule precedence is top-to-bottom.'
      menuItems={menuItems}
      {...frontendSimpleFilter}
      rowDragState={rowDragState}
      onRowMouseMove={onRowMouseMove}
      onRowMouseLeave={draggable.onRowMouseLeave}
    />
  );
};
